import { useMemo } from "react";
import { Card } from "primereact/card";
import { ProgressBar } from "primereact/progressbar";
import { themeClasses } from "sj-ui-toolkit/src/atlas/utils";
import {
  PROFICIENCY_THRESHOLDS,
  PROFICIENCY_COLORS,
  type TopicScore,
} from "@/constants/assessmentsReport.constants";
import { getProficiencyFromScore } from "@/helpers/assessmentsReport";
import styles from "./CategoryCard.module.scss";

export type { TopicScore };

interface CategoryCardProps {
  category: string;
  averageScore: number;
  topicScores: TopicScore[] | undefined;
  isCollapsed: boolean;
}

export default function CategoryCard({
  category,
  averageScore,
  topicScores,
  isCollapsed,
}: CategoryCardProps) {
  const { growthOpportunities, proficiencies, strengths } = useMemo(() => {
    const sorted = [...(topicScores ?? [])].sort((a, b) => a.averageScore - b.averageScore);
    return {
      growthOpportunities: sorted.filter(
        t => t.averageScore < PROFICIENCY_THRESHOLDS.GROWTH_OPPORTUNITIES
      ),
      proficiencies: sorted.filter(
        t =>
          t.averageScore >= PROFICIENCY_THRESHOLDS.GROWTH_OPPORTUNITIES &&
          t.averageScore < PROFICIENCY_THRESHOLDS.PROFICIENCIES
      ),
      strengths: sorted.filter(t => t.averageScore >= PROFICIENCY_THRESHOLDS.PROFICIENCIES),
    };
  }, [topicScores]);

  const clampedScore = Math.min(100, Math.max(0, averageScore));
  const overallScoreProficiency = getProficiencyFromScore(clampedScore);

  return (
    <Card className={`h-full ${themeClasses.bgElevation3}`}>
      <h3 className="body-lg font-semibold mb-1">{category}</h3>
      <p className={`body-sm ${themeClasses.textSecondary} mb-3`}>Proficiency Score Breakdown</p>
      <ProgressBar
        value={clampedScore}
        showValue
        color={overallScoreProficiency ? PROFICIENCY_COLORS[overallScoreProficiency] : undefined}
        className={`rounded-lg ${styles.progressBar}`}
      />

      <div
        className={`grid transition-[grid-template-rows] duration-700 ease-in-out ${
          isCollapsed ? "grid-rows-[0fr]" : "grid-rows-[1fr]"
        }`}
      >
        <div className="overflow-hidden">
          {growthOpportunities.length > 0 && (
            <>
              <hr className="my-4" />
              <h4 className="body-sm font-semibold mb-2">Growth Opportunities</h4>
              {growthOpportunities.map(topic => (
                <div key={topic.topic} className={styles.topicRow}>
                  <ProgressBar
                    value={topic.averageScore}
                    showValue={false}
                    color={PROFICIENCY_COLORS.foundational}
                    className={`rounded-lg ${styles.progressBar}`}
                    style={{ height: "7px" }}
                  />
                  <span className="body-sm font-semibold">{topic.averageScore}%</span>
                  <span className="body-xs truncate">{topic.topic}</span>
                </div>
              ))}
            </>
          )}

          {proficiencies.length > 0 && (
            <>
              <hr className="my-4" />
              <h4 className="body-sm font-semibold mb-2">Proficiencies</h4>
              {proficiencies.map(topic => (
                <div key={topic.topic} className={styles.topicRow}>
                  <ProgressBar
                    value={topic.averageScore}
                    showValue={false}
                    color={PROFICIENCY_COLORS.intermediate}
                    className={`rounded-lg ${styles.progressBar}`}
                    style={{ height: "7px" }}
                  />
                  <span className="body-sm font-semibold">{topic.averageScore}%</span>
                  <span className="body-xs truncate">{topic.topic}</span>
                </div>
              ))}
            </>
          )}

          {strengths.length > 0 && (
            <>
              <hr className="my-4" />
              <h4 className="body-sm font-semibold mb-2">Strengths</h4>
              {strengths.map(topic => (
                <div key={topic.topic} className={styles.topicRow}>
                  <ProgressBar
                    value={topic.averageScore}
                    showValue={false}
                    color={PROFICIENCY_COLORS.advanced}
                    className={`rounded-lg ${styles.progressBar}`}
                    style={{ height: "7px" }}
                  />
                  <span className="body-sm font-semibold">{topic.averageScore}%</span>
                  <span className="body-xs truncate">{topic.topic}</span>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </Card>
  );
}
